import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Activity,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Bath,
  Bell,
  Check,
  ChevronRight,
  Eye,
  FileText,
  FileUp,
  Heart,
  Info,
  Minus,
  MessageCircle,
  Paperclip,
  Pill,
  Plus,
  PersonStanding,
  Receipt,
  Soup,
  Sparkles,
  Upload,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useFamilyAuth } from "../../context/FamilyAuthContext";
import { FamilyPushSettings } from "../../components/PushNotifications";
import { SegmentedTabs, TabPanel } from "../../components/family/SegmentedTabs";
import { FamilyFileViewer, useFamilyFileOpener } from "../../components/family/FamilyFileViewer";
import { DropLabel, UploadQueue, useMultiUpload } from "../../components/family/FamilyMultiUpload";
import { NotificationRow, NotificationStates } from "../../components/family/FamilyNotificationBell";
import { useNotificationList } from "../../components/family/familyNotifications";

const statusLabels = {
  in_miglioramento: { Icon: ArrowUp, label: "In miglioramento", textClass: "text-emerald-600", background: "bg-emerald-50" },
  stabile: { Icon: ArrowRight, label: "Stabile", textClass: "text-[#4f7cac]", background: "bg-slate-50" },
  obiettivo_mantenuto: { Icon: ArrowRight, label: "Mantenuta", textClass: "text-[#4f7cac]", background: "bg-slate-50" },
  obiettivo_raggiunto: { Icon: Check, label: "Obiettivo raggiunto", textClass: "text-emerald-600", background: "bg-emerald-50" },
  da_rivalutare: { Icon: Minus, label: "Da rivalutare", textClass: "text-amber-600", background: "bg-amber-50" },
  da_monitorare: { Icon: ArrowDown, label: "Da monitorare", textClass: "text-rose-600", background: "bg-rose-50" },
};
const medicationLabels = {
  disponibile: ["Disponibile", "bg-emerald-500"],
  in_preparazione: ["In preparazione", "bg-amber-400"],
  ricetta_richiesta: ["Ricetta richiesta", "bg-rose-500"],
};
const areaIcons = {
  motoria: PersonStanding,
  mobilita: PersonStanding,
  nutrizione: Soup,
  alimentazione: Soup,
  igiene: Bath,
  autonomia: Bath,
  educativa: Users,
  socializzazione: Users,
  riposo: Heart,
  benessere: Heart,
  osservazione: Eye,
};

function AreaGlyph({ area }) {
  const Icon = areaIcons[area.area_pai] || areaIcons[area.id] || Heart;
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e7f3f1] text-[#2f6f6a]">
      <Icon className="h-5 w-5" strokeWidth={2} />
    </div>
  );
}

function useFamilyData(path, fallback = null) {
  const { api, activeOspiteId } = useFamilyAuth();
  const [data, setData] = useState(fallback); const [loading, setLoading] = useState(true); const [error, setError] = useState("");
  const latest = useRef(0);
  const reload = useCallback(async () => {
    const request = ++latest.current; // ignora le risposte superate (cambio ospite / pagina)
    if (!activeOspiteId || !path) { setLoading(false); return; }
    setLoading(true);
    try {
      const response = await api.get(path.replace(":id", activeOspiteId));
      if (request !== latest.current) return;
      setData(response.data); setError("");
    } catch (e) {
      if (request !== latest.current) return;
      setData(fallback); setError(e.response?.data?.detail || "Non è stato possibile caricare i dati");
    } finally {
      if (request === latest.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, path, activeOspiteId]);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, error, reload };
}

function formatDate(value, options = { day: "2-digit", month: "short", year: "numeric" }) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("it-IT", options);
}

function PageHeader({ title, subtitle, action, back = false, backTo = "/family" }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div className="flex min-w-0 gap-3">
        {back ? <Link to={backTo} className="mt-0.5 rounded-full p-1 text-[#314b6c] hover:bg-white"><ArrowLeft className="h-5 w-5" /></Link> : null}
        <div>
          <h1 className="text-[22px] font-extrabold tracking-tight text-[#243952] sm:text-2xl">{title}</h1>
          {subtitle ? <p className="mt-0.5 text-xs text-[#687c94] sm:text-sm">{subtitle}</p> : null}
        </div>
      </div>
      {action}
    </div>
  );
}

function Card({ children, className = "" }) {
  return <section className={`family-card ${className}`}>{children}</section>;
}

function Loading() {
  return <div className="py-20 text-center text-sm text-[#72839a]">Caricamento delle informazioni…</div>;
}

function Message({ error }) {
  return error ? <p className="family-card border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null;
}

function SectionLink({ to, title, action = "Vedi tutto", children }) {
  return (
    <section>
      <div className="mb-2.5 flex items-center justify-between">
        <h2 className="text-base font-extrabold text-[#273d58]">{title}</h2>
        <Link to={to} className="flex items-center text-xs font-bold text-[#6e48b5]">{action}<ChevronRight className="h-4 w-4" /></Link>
      </div>
      {children}
    </section>
  );
}

function StatusGlyph({ Icon, className }) {
  return Icon ? <Icon className={className} strokeWidth={2.2} /> : <span className="h-2.5 w-2.5 rounded-full bg-current" />;
}

function StatusTile({ area, compact = false }) {
  const { Icon, label, textClass, background } = statusLabels[area.stato] || statusLabels.stabile;
  if (compact) {
    return (
      <div className="flex flex-col rounded-2xl border border-[#d8e2ec] bg-white px-3 py-2.5">
        <p className="truncate text-sm font-extrabold text-[#263952]">{area.titolo}</p>
        <div className={`flex items-center justify-center py-2 ${textClass}`}>
          <StatusGlyph Icon={Icon} className="h-7 w-7" />
        </div>
        <p className={`text-center text-xs font-semibold ${textClass}`}>{label}</p>
      </div>
    );
  }
  return (
    <div className={`rounded-xl ${background} p-3`}>
      <p className="truncate text-xs font-bold text-[#304762]">{area.titolo}</p>
      <p className={`mt-1 flex items-center gap-1 text-[11px] font-bold ${textClass}`}>
        <StatusGlyph Icon={Icon} className="h-4 w-4" />
        {label}
      </p>
    </div>
  );
}

function StatusLegend({ areas }) {
  const counts = useMemo(() => areas.reduce((acc, area) => {
    acc[area.stato] = (acc[area.stato] || 0) + 1;
    return acc;
  }, {}), [areas]);
  return (
    <div className="grid grid-cols-3 gap-2 border-t border-slate-100 pt-3 text-center text-[10px] text-[#60738c] sm:grid-cols-5">
      {[
        ["obiettivo_raggiunto", "raggiunto", "bg-emerald-500"],
        ["in_miglioramento", "in miglioramento", "bg-[#6e48b5]"],
        ["obiettivo_mantenuto", "mantenuti", "bg-[#8b75c8]"],
        ["stabile", "stabili", "bg-[#aab8c7]"],
        ["da_rivalutare", "da rivalutare", "bg-amber-400"],
      ].map(([key, label, color]) => <div key={key}><span className={`mx-auto mb-1 block h-2 w-2 rounded-full ${color}`} /><b className="block text-[#304762]">{counts[key] || 0}</b>{label}</div>)}
    </div>
  );
}

function EmptyState({ icon: Icon = Info, title, text }) {
  return <Card className="p-7 text-center"><Icon className="mx-auto h-7 w-7 text-[#8f7ac8]" /><p className="mt-3 font-bold text-[#304762]">{title}</p><p className="mx-auto mt-1 max-w-sm text-sm leading-6 text-[#72839a]">{text}</p></Card>;
}

function familyGreeting(nome) {
  const hour = new Date().getHours();
  const saluto = hour < 12 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";
  const firstName = String(nome || "").trim();
  return firstName ? `${saluto}, ${firstName}` : saluto;
}

export function FamilyHome() {
  const { hasPermission, user, activeDelega } = useFamilyAuth();
  const { data, loading, error } = useFamilyData("/ospiti/:id/home", {});
  if (loading) return <Loading />;
  if (error) return <Message error={error} />;
  const { ospite = {}, percorso = [], farmaci = {}, documenti_richiesti, richieste_aperte, momenti = [], aggiornamenti = [] } = data || {};
  const residenza = ospite.struttura || activeDelega?.struttura_nome || "";
  return <div className="space-y-5">
    <div>
      <h1 className="text-xl font-extrabold text-[#263952]">{familyGreeting(user?.nome)}</h1>
      <p className="mt-0.5 text-sm text-[#7890aa]">Ecco le ultime novità su</p>
    </div>
    <Card className="overflow-hidden p-0">
      <div className="flex items-center gap-3 p-4">
        <img src={ospite.foto_url || "/images/logo-family-mark.png"} alt="" className="h-12 w-12 rounded-xl object-cover" />
        <div className="min-w-0 flex-1"><p className="truncate text-sm font-extrabold text-[#304762]">{ospite.nome || "Persona assistita"}</p>{residenza ? <p className="mt-0.5 text-xs text-[#71839a]">{residenza}</p> : null}</div>
      </div>
      <div className="border-t border-slate-100 bg-[#f5fbfa] px-4 py-3"><p className="flex items-center gap-2 text-sm font-bold text-emerald-700"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Tutto regolare</p><p className="mt-1 pl-[18px] text-[10px] text-[#72839a]">Ultimo aggiornamento: {formatDate(ospite.ultimo_aggiornamento)}</p></div>
    </Card>
    {hasPermission("percorso:read") && <SectionLink to="/family/percorso" title="Il suo percorso" action="Vedi tutti">{percorso.length ? <div className="grid grid-cols-2 gap-2.5">{percorso.slice(0, 4).map((area) => <StatusTile key={area.area_pai} area={area} compact />)}</div> : <Card className="p-4 text-sm text-[#72839a]">La struttura non ha ancora condiviso obiettivi del percorso.</Card>}</SectionLink>}
    {hasPermission("terapia:read") && <SectionLink to="/family/farmaci" title="Farmaci e terapia" action="Vedi"><Card className="p-4"><p className="text-xs font-bold text-[#304762]">{new Date().toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</p><div className="mt-3 grid grid-cols-3 divide-x divide-slate-100"><Count n={farmaci.disponibile} label="disponibili" color="bg-emerald-500" /><Count n={farmaci.in_preparazione} label="in preparazione" color="bg-amber-400" /><Count n={farmaci.ricetta_richiesta} label="ricette richieste" color="bg-rose-500" /></div></Card></SectionLink>}
    <div className="grid grid-cols-2 gap-3">
      {hasPermission("documenti:read") && <Link to="/family/documenti"><Card className="h-full p-4"><FileUp className="h-5 w-5 text-[#6e48b5]" /><p className="mt-2 text-sm font-bold text-[#304762]">Documenti</p><p className="text-xs text-[#72839a]">{documenti_richiesti || 0} da inviare</p></Card></Link>}
      {hasPermission("richieste:read") && <Link to="/family/richieste"><Card className="h-full p-4"><MessageCircle className="h-5 w-5 text-[#6e48b5]" /><p className="mt-2 text-sm font-bold text-[#304762]">Messaggi</p><p className="text-xs text-[#72839a]">{richieste_aperte || 0} richieste aperte</p></Card></Link>}
    </div>
    {(aggiornamenti.length > 0 || momenti.length > 0) && <SectionLink to="/family/aggiornamenti" title="Gli ultimi aggiornamenti"><Card className="divide-y divide-slate-100 px-4">{[...aggiornamenti, ...momenti].slice(0, 3).map((entry) => <p key={entry.id} className="py-3 text-sm font-medium text-[#425a76]">{entry.titolo}</p>)}</Card></SectionLink>}
  </div>;
}
function Count({ n = 0, label, color }) { return <div className="px-1 text-center"><span className={`mx-auto mb-1 block h-2 w-2 rounded-full ${color}`} /><b className="block text-sm text-[#304762]">{n || 0}</b><span className="mt-0.5 block text-[10px] leading-3 text-[#72839a]">{label}</span></div>; }

const weekdayLabels = { 0: "Lun", 1: "Mar", 2: "Mer", 3: "Gio", 4: "Ven", 5: "Sab", 6: "Dom", lun: "Lun", mar: "Mar", mer: "Mer", gio: "Gio", ven: "Ven", sab: "Sab", dom: "Dom" };

function formatTherapyDays(giorni) {
  if (!Array.isArray(giorni) || !giorni.length) return "";
  return giorni.map((day) => {
    if (typeof day === "number") return weekdayLabels[day] || "";
    const key = String(day).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").slice(0, 3);
    return weekdayLabels[key] || String(day);
  }).filter(Boolean).join(", ");
}

function therapyExtraRows(item) {
  const rows = [];
  const days = formatTherapyDays(item.giorni_settimana);
  if (days) rows.push(["Giorni", days]);
  if (String(item.note || "").trim()) rows.push(["Note", String(item.note).trim()]);
  if (item.data_inizio) rows.push(["Dal", formatDate(item.data_inizio)]);
  if (item.data_fine) rows.push(["Fino al", formatDate(item.data_fine)]);
  return rows;
}

function TherapyItem({ item }) {
  const [open, setOpen] = useState(false);
  const extras = therapyExtraRows(item);
  const expandable = extras.length > 0;
  return (
    <Card className="overflow-hidden p-0">
      <button
        type="button"
        disabled={!expandable}
        onClick={() => setOpen((value) => !value)}
        className={`flex w-full items-center justify-between gap-3 p-4 text-left ${expandable ? "" : "cursor-default"}`}
        aria-expanded={expandable ? open : undefined}
      >
        <div className="min-w-0">
          <p className="text-sm font-bold text-[#304762]">{item.farmaco}</p>
          <p className="mt-0.5 text-xs text-[#72839a]">{[item.dosaggio, (item.orari || []).join(" · ")].filter(Boolean).join(" · ") || "Dettaglio non indicato"}</p>
        </div>
        {expandable ? <ChevronRight className={`h-5 w-5 shrink-0 text-[#8ea0b3] transition-transform ${open ? "rotate-90" : ""}`} /> : null}
      </button>
      {open && expandable ? (
        <div className="space-y-2 border-t border-slate-100 bg-[#f8fbfe] px-4 py-3">
          {extras.map(([label, value]) => (
            <p key={label} className="text-xs leading-5 text-[#536b86]"><span className="font-bold text-[#304762]">{label}: </span>{value}</p>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

const PERCORSO_TABS = [
  { value: "panoramica", label: "Panoramica" },
  { value: "dettaglio", label: "Dettaglio" },
  { value: "documento", label: "Documento" },
];
const direzioneLabels = { migliorare: "Migliorare", mantenere: "Mantenere" };
const paiStatoLabels = { attivo: "Attivo", in_corso: "Attivo", scaduto: "Scaduto", bozza: "In definizione", archiviato: "Archiviato", sostituito: "Sostituito" };

function paiStatoLabel(stato) {
  if (!stato) return "—";
  return paiStatoLabels[stato] || String(stato).replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase());
}

function withUnit(value, unit) {
  if (!value) return "";
  return unit && /^[\d.,\s]+$/.test(String(value)) ? `${value} ${unit}` : String(value);
}

function AreaHeader({ area }) {
  const { Icon, label, textClass } = statusLabels[area.stato] || statusLabels.stabile;
  return (
    <div className="flex items-start gap-3">
      <AreaGlyph area={area} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-extrabold text-[#304762]">{area.titolo}</p>
        <p className={`mt-0.5 flex items-center gap-1 text-xs font-bold ${textClass}`}>
          <StatusGlyph Icon={Icon} className="h-3.5 w-3.5" />
          {label}
        </p>
      </div>
    </div>
  );
}

function PercorsoPanoramica({ data, areas }) {
  if (!areas.length) return <EmptyState icon={Heart} title="Percorso in preparazione" text="La struttura non ha ancora condiviso obiettivi del percorso." />;
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div><p className="text-sm font-bold text-[#304762]">PAI attuale</p><p className="mt-0.5 text-xs text-[#72839a]">{formatDate(data?.pai?.data_redazione)} → {formatDate(data?.pai?.data_scadenza)}</p></div>
        <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#647995]">Obiettivi</p>
        <StatusLegend areas={areas} />
      </Card>
      <section>
        <h2 className="mb-2.5 text-base font-extrabold text-[#273d58]">Andamento per area</h2>
        <div className="space-y-3">{areas.map((area) => (
          <Card key={area.area_pai} className="p-4">
            <AreaHeader area={area} />
            <p className="mt-2 text-xs leading-5 text-[#677b93]">{area.descrizione || "Obiettivo in aggiornamento."}</p>
            {area.trend?.length > 1 ? <div className="mt-3 h-24"><ResponsiveContainer><LineChart data={area.trend}><XAxis dataKey="data" hide /><YAxis domain={[0, 2]} hide /><Tooltip /><Line type="monotone" dataKey="score" stroke="#6e48b5" strokeWidth={2.5} dot={{ r: 3, fill: "#6e48b5" }} /></LineChart></ResponsiveContainer></div> : null}
            <p className="mt-3 text-[11px] text-[#72839a]">Prossima verifica</p>
            <p className="text-xs font-semibold text-[#405873]">{formatDate(area.prossima_verifica)}</p>
          </Card>
        ))}</div>
      </section>
    </div>
  );
}

function DetailItem({ label, children, wide = false }) {
  return <div className={wide ? "col-span-2" : ""}><dt className="font-bold text-[#304762]">{label}</dt><dd className="mt-0.5 text-[#536b86]">{children}</dd></div>;
}

function PercorsoDettaglio({ areas }) {
  if (!areas.length) return <EmptyState icon={Heart} title="Nessun dettaglio disponibile" text="Quando la struttura condividerà gli obiettivi del percorso, ne vedrai qui i dettagli." />;
  return (
    <section aria-label="Dettaglio degli obiettivi">
      <h2 className="mb-2.5 text-base font-extrabold text-[#273d58]">Obiettivi in dettaglio</h2>
      <div className="space-y-3">{areas.map((area) => (
        <Card key={area.area_pai} className="p-4">
          <AreaHeader area={area} />
          <p className="mt-2 text-xs leading-5 text-[#677b93]">{area.descrizione || "Obiettivo in aggiornamento."}</p>
          <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 text-xs">
            <DetailItem label="Obiettivo">{direzioneLabels[area.direzione] || "—"}</DetailItem>
            <DetailItem label="Prossima verifica">{formatDate(area.prossima_verifica)}</DetailItem>
            {area.baseline ? <DetailItem label="Punto di partenza">{withUnit(area.baseline, area.unita)}</DetailItem> : null}
            {area.target ? <DetailItem label="Traguardo">{withUnit(area.target, area.unita)}</DetailItem> : null}
            <DetailItem label="Ultimo aggiornamento" wide>{area.ultima_valutazione ? formatDate(area.ultima_valutazione.data) : "Non ancora valutato"}</DetailItem>
          </dl>
        </Card>
      ))}</div>
    </section>
  );
}

function PercorsoDocumento({ data, areas }) {
  const { hasPermission } = useFamilyAuth();
  const pai = data?.pai;
  const revisioni = data?.revisioni || [];
  if (!pai && !revisioni.length) return <EmptyState icon={FileText} title="Documento non ancora condiviso" text="Il Piano Assistenziale Individualizzato comparirà qui quando la struttura lo renderà disponibile." />;
  return (
    <div className="space-y-4">
      {pai ? (
        <Card className="p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EDE9F6]"><FileText className="h-5 w-5 text-[#6e48b5]" /></span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-extrabold text-[#304762]">Piano Assistenziale Individualizzato</p>
              <p className="mt-0.5 text-xs text-[#72839a]">Documento di riferimento del percorso</p>
            </div>
            <span className="rounded-full bg-[#EDE9F6] px-2 py-0.5 text-[10px] font-bold text-[#5a3b94]">{paiStatoLabel(pai.stato)}</span>
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 text-xs">
            <DetailItem label="Redatto il">{formatDate(pai.data_redazione)}</DetailItem>
            <DetailItem label="Valido fino al">{formatDate(pai.data_scadenza)}</DetailItem>
          </dl>
        </Card>
      ) : null}
      {areas.length ? (
        <section>
          <h2 className="mb-2.5 text-base font-extrabold text-[#273d58]">Obiettivi condivisi</h2>
          <Card className="divide-y divide-slate-100 px-4">{areas.map((area) => (
            <div key={area.area_pai} className="py-3">
              <p className="text-sm font-bold text-[#304762]">{area.titolo}</p>
              <p className="mt-0.5 text-xs leading-5 text-[#677b93]">{area.descrizione || "Obiettivo in aggiornamento."}</p>
            </div>
          ))}</Card>
        </section>
      ) : null}
      {revisioni.length ? (
        <section>
          <h2 className="mb-2.5 text-base font-extrabold text-[#273d58]">Versioni precedenti</h2>
          <Card className="divide-y divide-slate-100 px-4">{revisioni.map((item, index) => (
            <div key={item.id || index} className="flex items-center justify-between gap-3 py-3 text-sm">
              <span className="text-[#405873]">{formatDate(item.data_redazione)} → {formatDate(item.data_scadenza)}</span>
              <span className="text-xs text-[#657990]">{paiStatoLabel(item.stato)}</span>
            </div>
          ))}</Card>
        </section>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-2">
        {hasPermission("documenti:read") ? <Link to="/family/documenti?tab=ricevuti" className="flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-sm font-bold text-[#6e48b5]">Vai ai documenti ricevuti</Link> : null}
        {hasPermission("richieste:write") ? <Link to="/family/richieste?nuova=pai" className="flex h-11 items-center justify-center rounded-xl bg-[#6e48b5] text-sm font-bold text-white hover:bg-[#5a3b94]">Richiedi una copia del PAI</Link> : null}
      </div>
    </div>
  );
}

export function FamilyPercorso() {
  const { data, loading, error } = useFamilyData("/ospiti/:id/percorso", {});
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const tab = PERCORSO_TABS.some((item) => item.value === requested) ? requested : "panoramica";
  const changeTab = (value) => setSearchParams(value === "panoramica" ? {} : { tab: value }, { replace: true });
  if (loading) return <Loading />;
  if (error) return <div className="space-y-4"><PageHeader back title="Il suo percorso" /><Message error={error} /></div>;
  const areas = data?.aree || [];
  return (
    <div className="space-y-4">
      <PageHeader back title="Il suo percorso" subtitle="Tutto ciò che l'équipe ha scelto di condividere." />
      <SegmentedTabs idPrefix="percorso" ariaLabel="Sezioni del percorso" tabs={PERCORSO_TABS} value={tab} onChange={changeTab} />
      <TabPanel idPrefix="percorso" value="panoramica" active={tab}><PercorsoPanoramica data={data} areas={areas} /></TabPanel>
      <TabPanel idPrefix="percorso" value="dettaglio" active={tab}><PercorsoDettaglio areas={areas} /></TabPanel>
      <TabPanel idPrefix="percorso" value="documento" active={tab}><PercorsoDocumento data={data} areas={areas} /></TabPanel>
    </div>
  );
}

function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  return Array.isArray(detail) ? detail.map((item) => item.msg || item).join(" ") : detail || fallback;
}

const FARMACI_TABS = [
  { value: "terapia", label: "Terapia" },
  { value: "mese", label: "Situazione mese" },
  { value: "ricette", label: "Ricette" },
];
const ricettaStatoLabels = { da_verificare: "Da verificare", caricata: "Caricata" };

export function FamilyFarmaci() {
  const { activeOspiteId, hasPermission, api } = useFamilyAuth();
  const terapia = useFamilyData("/ospiti/:id/farmaci/terapia", {});
  const situazione = useFamilyData("/ospiti/:id/farmaci/situazione-mese", {});
  const ricette = useFamilyData("/ospiti/:id/farmaci/ricette", {});
  const files = useFamilyFileOpener(api);
  const [tab, setTab] = useState("terapia");
  const [file, setFile] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const upload = async () => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/ospiti/${activeOspiteId}/farmaci/ricette`, form);
      toast.success("Ricetta inviata alla struttura");
      setFile(null);
      setInputKey((value) => value + 1);
      ricette.reload();
    } catch (err) {
      toast.error(apiError(err, "Non è stato possibile inviare la ricetta"));
    } finally {
      setUploading(false);
    }
  };
  const monthItems = situazione.data?.items || [];
  const monthLabel = new Date().toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const currentContent = tab === "terapia" ? (
    terapia.loading ? <Loading /> : terapia.error ? <Message error={terapia.error} /> : <div className="space-y-2"><p className="px-1 text-[11px] text-[#72839a]">Aggiornata al {formatDate(terapia.data?.aggiornata_at)}</p>{terapia.data?.items?.length ? terapia.data.items.map((item) => <TherapyItem key={item.id} item={item} />) : <EmptyState icon={Pill} title="Nessuna terapia condivisa" text="La struttura non ha ancora pubblicato informazioni sulla terapia." />}</div>
  ) : tab === "mese" ? (
    situazione.loading ? <Loading /> : situazione.error ? <Message error={situazione.error} /> : <div className="space-y-4"><Card className="p-4"><p className="text-base font-extrabold text-[#304762]">{monthLabel}</p><p className="mt-0.5 text-xs text-[#72839a]">{monthItems.length} farmaci in terapia</p><div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-slate-100">{Object.entries(situazione.data?.riepilogo || {}).map(([key, amount]) => <i key={key} className={medicationLabels[key]?.[1]} style={{ width: `${monthItems.length ? (amount / monthItems.length) * 100 : 0}%` }} />)}</div><div className="mt-4 grid grid-cols-3"><Count n={situazione.data?.riepilogo?.disponibile} label="disponibili" color="bg-emerald-500" /><Count n={situazione.data?.riepilogo?.in_preparazione} label="in preparazione" color="bg-amber-400" /><Count n={situazione.data?.riepilogo?.ricetta_richiesta} label="ricette richieste" color="bg-rose-500" /></div></Card><section><h2 className="mb-2.5 text-base font-extrabold text-[#273d58]">Dettaglio farmaci</h2>{monthItems.length ? <Card className="divide-y divide-slate-100 px-4">{monthItems.map((item) => <div key={item.terapia_id} className="flex items-center justify-between py-3 text-sm"><span className="font-medium text-[#405873]">{item.farmaco}</span><span className="flex items-center gap-1.5 text-xs text-[#657990]"><i className={`h-2 w-2 rounded-full ${medicationLabels[item.stato]?.[1]}`} />{medicationLabels[item.stato]?.[0]}</span></div>)}</Card> : <EmptyState icon={Pill} title="Nessun farmaco in terapia" text="Quando la struttura pubblicherà la terapia, qui vedrai la situazione del mese." />}</section></div>
  ) : (
    ricette.loading ? <Loading /> : <div className="space-y-3"><Message error={ricette.error} />{ricette.data?.items?.map((item) => (
      <Card key={item.id} className="flex items-center gap-3 p-4">
        <div className="min-w-0 flex-1"><p className="truncate font-bold text-[#304762]">{item.file_name || "Ricetta"}</p><p className="mt-1 text-xs text-[#72839a]">{ricettaStatoLabels[item.stato] || String(item.stato || "").replaceAll("_", " ")}{item.created_at ? ` · ${formatDate(item.created_at)}` : ""}</p></div>
        {item.file_disponibile ? <button type="button" onClick={() => files.open(`/ospiti/${activeOspiteId}/farmaci/ricette/${item.id}/download`, item.file_name, item.file_type)} className="rounded-lg bg-[#EDE9F6] px-2.5 py-2 text-xs font-bold text-[#6e48b5]" aria-label={`Apri ${item.file_name || "ricetta"}`}>Apri</button> : null}
      </Card>
    ))}{!ricette.error && !ricette.data?.items?.length ? <EmptyState icon={Pill} title="Nessuna ricetta" text={hasPermission("ricette:write") ? "Carica la foto o il PDF di una ricetta per inviarla alla struttura." : "Qui vedrai le ricette condivise con la struttura."} /> : null}{hasPermission("ricette:write") ? <Card className="border-dashed border-[#c9bce3] bg-[#fcfbfe] p-4"><label className="flex cursor-pointer items-center justify-center gap-2 text-sm font-bold text-[#6e48b5]"><Plus className="h-5 w-5 rounded-full bg-[#6e48b5] p-1 text-white" />{file ? file.name : "Carica una ricetta"}<input key={inputKey} className="sr-only" type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} /></label>{file ? <Button disabled={uploading} onClick={upload} className="mt-3 h-10 w-full rounded-xl bg-[#6e48b5] hover:bg-[#5a3b94]"><Upload className="mr-2 h-4 w-4" />{uploading ? "Caricamento…" : "Invia ricetta"}</Button> : null}</Card> : null}</div>
  );
  return <div><PageHeader back title="Farmaci e terapia" subtitle="Terapia attuale e stato dei farmaci." /><SegmentedTabs idPrefix="farmaci" ariaLabel="Sezioni farmaci" className="mb-4" tabs={FARMACI_TABS} value={tab} onChange={setTab} /><div role="tabpanel" id={`farmaci-panel-${tab}`} aria-labelledby={`farmaci-tab-${tab}`}>{currentContent}</div><FamilyFileViewer viewer={files.viewer} onClose={files.close} /></div>;
}

const DOCUMENTI_TABS = [
  { value: "richiesti", label: "Da inviare" },
  { value: "ricevuti", label: "Ricevuti" },
  { value: "inviati", label: "Inviati" },
];

export function FamilyDocumenti() {
  const { activeOspiteId, hasPermission, api } = useFamilyAuth();
  const { data, loading, error, reload } = useFamilyData("/ospiti/:id/documenti", {});
  const files = useFamilyFileOpener(api);
  const [searchParams] = useSearchParams();
  const [inputKey, setInputKey] = useState(0);
  const [tab, setTab] = useState(() => (DOCUMENTI_TABS.some((item) => item.value === searchParams.get("tab")) ? searchParams.get("tab") : "richiesti"));
  const queue = useMultiUpload({ api, ospiteId: activeOspiteId, onFinished: reload });
  const add = (fileList, extra = {}) => { queue.addFiles(fileList, extra); setInputKey((value) => value + 1); };
  if (loading) return <Loading />;
  const richieste = (data?.richieste || []).filter((req) => ["richiesta", "da_integrare"].includes(req.stato));
  const ricevuti = (data?.documenti || []).filter((doc) => doc.origine !== "family" && doc.visibile_family);
  const inviati = (data?.documenti || []).filter((doc) => doc.origine === "family");
  const list = tab === "ricevuti" ? ricevuti : inviati;
  const tabs = DOCUMENTI_TABS.map((item) => (item.value === "richiesti" ? { ...item, badge: richieste.length || null } : item));
  const canUpload = hasPermission("documenti:write");
  return (
    <div className="space-y-4">
      <PageHeader back title="Documenti" subtitle="Tutto viene archiviato e tracciato." />
      <SegmentedTabs idPrefix="documenti" ariaLabel="Sezioni documenti" tabs={tabs} value={tab} onChange={setTab} />
      <Message error={error} />
      <UploadQueue queue={queue} />
      <div role="tabpanel" id={`documenti-panel-${tab}`} aria-labelledby={`documenti-tab-${tab}`} className="space-y-3">
        {tab === "richiesti" ? (
          richieste.length ? richieste.map((req) => (
            <Card key={req.id} className="p-4">
              <div className="flex gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EDE9F6]"><FileText className="h-5 w-5 text-[#6e48b5]" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-bold text-[#304762]">{req.titolo}</p>
                    <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600">{req.stato === "da_integrare" ? "Da integrare" : "Richiesta"}</span>
                  </div>
                  <p className="mt-1 text-xs text-[#72839a]">{req.descrizione}</p>
                  {req.scadenza ? <p className="mt-2 text-xs font-medium text-[#536b86]">Scadenza: {formatDate(req.scadenza)}</p> : null}
                </div>
              </div>
              {canUpload ? (
                <DropLabel
                  inputKey={`${inputKey}-${req.id}`}
                  ariaLabel={`Carica documento per ${req.titolo}`}
                  onFiles={(files) => add(files, { richiestaId: req.id, sostituisceFileId: req.file_id || null })}
                  className="mt-4 flex h-10 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-[#c9bce3] text-sm font-bold text-[#6e48b5] focus-within:ring-2 focus-within:ring-[#6e48b5]/40"
                >
                  <Plus className="h-4 w-4" />Carica documento (anche più file)
                </DropLabel>
              ) : null}
            </Card>
          )) : <EmptyState icon={Check} title="Nessun documento richiesto" text="La struttura non ti ha chiesto altri documenti." />
        ) : (
          <>
            {tab === "inviati" && canUpload ? (
              <Card className="border-dashed border-[#c9bce3] bg-[#fcfbfe] p-4">
                <DropLabel
                  inputKey={`${inputKey}-libero`}
                  ariaLabel="Invia un documento alla struttura"
                  onFiles={(files) => add(files)}
                  className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl py-2 text-sm font-bold text-[#6e48b5] focus-within:ring-2 focus-within:ring-[#6e48b5]/40"
                >
                  <span className="flex items-center gap-2"><Plus className="h-5 w-5 rounded-full bg-[#6e48b5] p-1 text-white" />Invia uno o più documenti alla struttura</span>
                  <span className="text-[11px] font-medium text-[#72839a]">Seleziona i file o trascinali qui</span>
                </DropLabel>
                <p className="mt-2 text-center text-[11px] text-[#72839a]">PDF, immagini, Word o Excel · max 50 MB ciascuno · fino a 10 file alla volta</p>
              </Card>
            ) : null}
            {list.length ? list.map((doc) => (
              <Card key={doc.file_id} className="flex items-center gap-3 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EDE9F6]"><FileText className="h-5 w-5 text-[#6e48b5]" /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-[#304762]">{doc.file_name}</p>
                  <p className="mt-0.5 text-xs capitalize text-[#72839a]">{(doc.stato_family || "caricato").replaceAll("_", " ")} · versione {doc.versione || 1}</p>
                </div>
                <button type="button" onClick={() => files.open(`/ospiti/${activeOspiteId}/documenti/${doc.file_id}/download`, doc.file_name, doc.file_type)} className="rounded-lg bg-[#EDE9F6] px-2.5 py-2 text-xs font-bold text-[#6e48b5]" aria-label={`Apri ${doc.file_name}`}>Apri</button>
              </Card>
            )) : (
              <EmptyState icon={Paperclip} title="Nessun documento" text={tab === "ricevuti" ? "Qui troverai i documenti che la struttura ha condiviso con te." : "Qui trovi i file che hai inviato."} />
            )}
          </>
        )}
      </div>
      <FamilyFileViewer viewer={files.viewer} onClose={files.close} />
    </div>
  );
}

export function FamilyNotifiche() {
  const { api, activeOspiteId } = useFamilyAuth();
  const [tab, setTab] = useState("tutte");
  const list = useNotificationList(api, activeOspiteId, { pageSize: 15, onlyUnread: tab === "non-lette" });
  const tabs = [{ value: "tutte", label: "Tutte" }, { value: "non-lette", label: "Non lette", badge: list.unread }];
  return (
    <div className="space-y-4">
      <PageHeader
        back
        backTo="/family/altro"
        title="Notifiche"
        subtitle="Le novità condivise dalla struttura, in ordine di arrivo."
        action={<Button type="button" variant="outline" size="sm" onClick={list.markAllRead} disabled={!list.unread} className="shrink-0">Segna tutte come lette</Button>}
      />
      <SegmentedTabs idPrefix="notifiche" ariaLabel="Filtra le notifiche" tabs={tabs} value={tab} onChange={setTab} />
      <TabPanel idPrefix="notifiche" value={tab} active={tab}>
        <Card className="p-2">
          <NotificationStates
            status={list.status}
            error={list.error}
            items={list.items}
            onRetry={list.load}
            emptyText={tab === "non-lette" ? "Hai letto tutto: nessuna notifica da leggere." : "Quando la struttura condividerà una novità la vedrai qui."}
          />
          {list.status === "ready" && list.items.length ? (
            <ul className="space-y-1">
              {list.items.map((item) => <NotificationRow key={item.id} item={item} onRead={list.markRead} onOpen={(n) => list.markRead(n.id)} />)}
            </ul>
          ) : null}
          {list.hasMore ? (
            <div className="p-2 text-center">
              <Button type="button" variant="outline" size="sm" onClick={list.loadMore} disabled={list.loadingMore}>{list.loadingMore ? "Caricamento…" : "Carica altre"}</Button>
            </div>
          ) : null}
        </Card>
      </TabPanel>
    </div>
  );
}

export function FamilyAggiornamenti() { const { data, loading, error } = useFamilyData("/ospiti/:id/aggiornamenti", {}); if (loading) return <Loading />; return <div className="space-y-4"><PageHeader back backTo="/family/altro" title="Aggiornamenti" subtitle="Notizie condivise dalla struttura." /><Message error={error} />{error ? null : data?.items?.length ? <div className="space-y-3">{data.items.map((item) => <Card key={item.id} className="p-4"><p className="text-[11px] font-bold capitalize text-[#6e48b5]">{item.tipo}</p><p className="mt-1 font-extrabold text-[#304762]">{item.titolo}</p><p className="mt-1 text-sm leading-6 text-[#60758f]">{item.testo}</p><p className="mt-3 text-[11px] text-[#8a9bb0]">{formatDate(item.data_evento)}</p></Card>)}</div> : <EmptyState icon={Activity} title="Nessun aggiornamento" text="Quando la struttura pubblicherà una novità, la vedrai qui." />}</div>; }
function MomentGallery({ momento, photos, alt = "", onOpen }) {
  const { api, activeOspiteId } = useFamilyAuth();
  const [srcs, setSrcs] = useState([]);
  const [index, setIndex] = useState(0);
  const photoKey = (photos || []).map((foto) => foto.id).join(",");
  useEffect(() => {
    let urls = [];
    let cancelled = false;
    setIndex(0);
    Promise.all((photos || []).map((foto) => api.get(`/ospiti/${activeOspiteId}/momenti/${momento.id}/foto/${foto.id}`, { responseType: "blob" })
      .then((response) => {
        const url = URL.createObjectURL(response.data);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return null;
        }
        urls.push(url);
        return url;
      })
      .catch(() => null))).then((loaded) => { if (!cancelled) setSrcs(loaded); });
    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [api, activeOspiteId, momento.id, photoKey]);
  const current = srcs[index];
  if (!photos?.length) return null;
  if (!current) return <div className="h-52 w-full bg-[#EDE9F6]" />;
  const go = (direction) => setIndex((value) => (value + direction + photos.length) % photos.length);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onOpen?.({ items: srcs.filter(Boolean), index: srcs.slice(0, index + 1).filter(Boolean).length - 1, alt })}
        className="block w-full cursor-zoom-in"
        aria-label="Visualizza la foto a schermo intero"
      >
        <img className="h-52 w-full object-cover" alt={alt} src={current} />
      </button>
      {photos.length > 1 ? (
        <>
          <span className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-semibold text-white">{index + 1}/{photos.length}</span>
          <button type="button" onClick={() => go(-1)} className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white hover:bg-black/60" aria-label="Foto precedente">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => go(1)} className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white hover:bg-black/60" aria-label="Foto successiva">
            <ArrowRight className="h-4 w-4" />
          </button>
        </>
      ) : null}
    </div>
  );
}

const MOMENTI_TABS = [
  { value: "tutti", label: "Tutti" },
  { value: "attivita", label: "Attività" },
  { value: "eventi", label: "Eventi" },
  { value: "uscite", label: "Uscite" },
];

export function FamilyMomenti() {
  const { data, loading, error } = useFamilyData("/ospiti/:id/momenti", {});
  const [tab, setTab] = useState("tutti");
  const [viewer, setViewer] = useState(null);
  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setViewer(null);
      if (event.key === "ArrowLeft" && viewer.items?.length > 1) {
        setViewer((current) => current ? { ...current, index: (current.index - 1 + current.items.length) % current.items.length } : current);
      }
      if (event.key === "ArrowRight" && viewer.items?.length > 1) {
        setViewer((current) => current ? { ...current, index: (current.index + 1) % current.items.length } : current);
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [viewer]);
  if (loading) return <Loading />;
  const items = (data?.items || []).filter((item) => tab === "tutti" || item.tipo === tab);
  return (
    <div className="space-y-4">
      <PageHeader back backTo="/family/altro" title="Momenti" subtitle="La vita di ogni giorno." />
      <SegmentedTabs idPrefix="momenti" ariaLabel="Tipo di momento" tabs={MOMENTI_TABS} value={tab} onChange={setTab} />
      <Message error={error} />
      <div role="tabpanel" id={`momenti-panel-${tab}`} aria-labelledby={`momenti-tab-${tab}`}>
      {error ? null : items.length ? (
        <div className="space-y-4">
          {items.map((item) => (
            <Card key={item.id} className="overflow-hidden p-0">
              {data.foto_abilitate && item.foto?.length ? (
                <MomentGallery momento={item} photos={item.foto} alt={item.titolo || ""} onOpen={setViewer} />
              ) : null}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-extrabold text-[#304762]">{item.titolo}</p>
                  <span className="shrink-0 whitespace-nowrap text-[11px] text-[#8a9bb0]">{formatDate(item.data_evento, { weekday: "short", day: "2-digit", month: "short" })}</span>
                </div>
                <p className="mt-1 text-sm leading-6 text-[#60758f]">{item.testo}</p>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState icon={Sparkles} title="Nessun momento da mostrare" text="Le attività e gli eventi condivisi dalla struttura appariranno qui." />
      )}
      </div>
      {viewer ? (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={viewer.alt || "Foto del momento"}
          onClick={() => setViewer(null)}
        >
          <button
            type="button"
            onClick={() => setViewer(null)}
            className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
            aria-label="Chiudi"
          >
            <X className="h-5 w-5" />
          </button>
          {viewer.items?.length > 1 ? (
            <>
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); setViewer((current) => current ? { ...current, index: (current.index - 1 + current.items.length) % current.items.length } : current); }}
                className="absolute left-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
                aria-label="Foto precedente"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); setViewer((current) => current ? { ...current, index: (current.index + 1) % current.items.length } : current); }}
                className="absolute right-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
                aria-label="Foto successiva"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
              <p className="absolute bottom-5 left-1/2 z-10 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs font-semibold text-white">
                {viewer.index + 1}/{viewer.items.length}
              </p>
            </>
          ) : null}
          <img
            src={viewer.items?.[viewer.index] || viewer.src}
            alt={viewer.alt}
            className="max-h-[88dvh] max-w-full rounded-xl object-contain"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
}

const PERMISSION_LABELS = {
  percorso: "Percorso", terapia: "Terapia", ricette: "Ricette", documenti: "Documenti", aggiornamenti: "Aggiornamenti",
  momenti: "Momenti", amministrazione: "Amministrazione", richieste: "Messaggi e richieste",
};

function permissionChips(permessi = []) {
  const areas = new Map();
  permessi.forEach((permission) => {
    const [area, mode] = String(permission).split(":");
    areas.set(area, { write: Boolean(areas.get(area)?.write) || mode === "write" });
  });
  return [...areas.entries()].map(([area, { write }]) => ({ key: area, label: `${PERMISSION_LABELS[area] || area}${write ? " · lettura e invio" : ""}` }));
}

function Field({ id, label, children }) {
  return <div className="space-y-1.5"><Label htmlFor={id} className="text-xs font-bold text-[#536b86]">{label}</Label>{children}</div>;
}

function ProfileForm() {
  const { user, api, updateUser } = useFamilyAuth();
  const [form, setForm] = useState({ nome: user?.nome || "", cognome: user?.cognome || "", telefono: user?.telefono || "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true); setError("");
    try {
      const { data } = await api.patch("/me", { nome: form.nome, cognome: form.cognome, telefono: form.telefono });
      updateUser(data);
      toast.success("Dati aggiornati");
    } catch (err) {
      setError(apiError(err, "Non è stato possibile salvare i dati"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card className="p-5">
      <h2 className="text-base font-extrabold text-[#273d58]">I tuoi dati</h2>
      <form onSubmit={submit} className="mt-3 space-y-3" aria-label="Modifica i tuoi dati">
        <div className="grid grid-cols-2 gap-3">
          <Field id="profilo-nome" label="Nome"><Input id="profilo-nome" required maxLength={100} value={form.nome} onChange={set("nome")} autoComplete="given-name" /></Field>
          <Field id="profilo-cognome" label="Cognome"><Input id="profilo-cognome" required maxLength={100} value={form.cognome} onChange={set("cognome")} autoComplete="family-name" /></Field>
        </div>
        <Field id="profilo-telefono" label="Telefono"><Input id="profilo-telefono" type="tel" maxLength={40} value={form.telefono} onChange={set("telefono")} autoComplete="tel" /></Field>
        <p className="text-[11px] text-[#72839a]">L&apos;email ({user?.email}) è il tuo accesso e non può essere modificata da qui.</p>
        {error ? <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
        <Button disabled={saving} className="h-10 w-full rounded-xl bg-[#6e48b5] hover:bg-[#5a3b94]">{saving ? "Salvataggio…" : "Salva i dati"}</Button>
      </form>
    </Card>
  );
}

function PasswordForm() {
  const { api } = useFamilyAuth();
  const empty = { current: "", next: "", confirm: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (form.next.length < 10) return setError("La nuova password deve avere almeno 10 caratteri");
    if (form.next !== form.confirm) return setError("Le password non coincidono");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password aggiornata");
      setForm(empty);
    } catch (err) {
      setError(apiError(err, "Non è stato possibile cambiare la password"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card className="p-5">
      <h2 className="text-base font-extrabold text-[#273d58]">Cambia password</h2>
      <form onSubmit={submit} className="mt-3 space-y-3" aria-label="Cambia password">
        <Field id="pwd-attuale" label="Password attuale"><Input id="pwd-attuale" type="password" required value={form.current} onChange={set("current")} autoComplete="current-password" /></Field>
        <Field id="pwd-nuova" label="Nuova password (almeno 10 caratteri)"><Input id="pwd-nuova" type="password" required minLength={10} value={form.next} onChange={set("next")} autoComplete="new-password" /></Field>
        <Field id="pwd-conferma" label="Conferma nuova password"><Input id="pwd-conferma" type="password" required minLength={10} value={form.confirm} onChange={set("confirm")} autoComplete="new-password" /></Field>
        {error ? <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
        <Button disabled={saving} variant="outline" className="h-10 w-full rounded-xl">{saving ? "Salvataggio…" : "Aggiorna password"}</Button>
      </form>
    </Card>
  );
}

export function FamilyProfilo() {
  const { user, token, activeDelega, logout } = useFamilyAuth();
  return (
    <div className="space-y-4">
      <PageHeader back backTo="/family/altro" title="Profilo" subtitle="Le tue informazioni e autorizzazioni." />
      <Card className="p-5">
        <div className="flex gap-3">
          <UserRound className="h-11 w-11 rounded-xl bg-[#EDE9F6] p-2 text-[#6e48b5]" />
          <div>
            <p className="font-extrabold text-[#304762]">{user?.nome} {user?.cognome}</p>
            <p className="mt-0.5 text-sm text-[#72839a]">{user?.email}</p>
          </div>
        </div>
        <div className="mt-5 border-t border-slate-100 pt-4">
          <p className="text-xs font-bold uppercase tracking-wide text-[#72839a]">Autorizzazioni per {activeDelega?.ospite_nome}</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {permissionChips(activeDelega?.permessi).map((chip) => (
              <span key={chip.key} className="rounded-full bg-[#EDE9F6] px-2.5 py-1 text-xs font-medium text-[#5a3b94]">{chip.label}</span>
            ))}
          </div>
        </div>
        <Button variant="outline" onClick={logout} className="mt-6 h-10 w-full rounded-xl">Esci dall'account</Button>
      </Card>
      <ProfileForm />
      <PasswordForm />
      <FamilyPushSettings token={token} />
    </div>
  );
}
export function FamilyAltro() { const { hasPermission } = useFamilyAuth(); const links = [{ to: "/family/momenti", label: "Momenti", icon: Sparkles, permission: "momenti:read" }, { to: "/family/aggiornamenti", label: "Aggiornamenti", icon: Activity, permission: "aggiornamenti:read" }, { to: "/family/richieste", label: "Messaggi e richieste", icon: MessageCircle, permission: "richieste:read" }, { to: "/family/amministrazione", label: "Amministrazione", icon: Receipt, permission: "amministrazione:read" }, { to: "/family/notifiche", label: "Notifiche", icon: Bell }, { to: "/family/profilo", label: "Profilo e impostazioni", icon: UserRound }]; return <div><PageHeader title="Altro" subtitle="Altre informazioni per te." /><div className="space-y-2">{links.filter((item) => !item.permission || hasPermission(item.permission)).map(({ to, label, icon: Icon }) => <Link key={to} to={to}><Card className="mb-4 flex items-center justify-between p-4"><div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EDE9F6]"><Icon className="h-5 w-5 text-[#6e48b5]" /></span><span className="font-bold text-[#304762]">{label}</span></div><ArrowRight className="h-4 w-4 text-[#8ea0b3]" /></Card></Link>)}</div></div>; }
